import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ROLE_LABELS } from '@unitly/contracts';
import {
  useAcceptInvitation, useForgotPassword, useInvitationPreview, useLogin, useLogout, useResendVerification, useResetPassword,
  useSignup, useVerifyEmail,
} from '../api/queries.ts';
import { ApiError } from '../api/client.ts';
import type { Me } from '@unitly/contracts';
import { BrandLogo, PageLoader, TextField } from '../ui/primitives.tsx';

const linkButton: React.CSSProperties = { color: 'var(--accent)', background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' };

const fieldErrors = (e: unknown) => (e instanceof ApiError ? e.errors : {});
const formError = (e: unknown) => (e instanceof ApiError && !Object.keys(e.errors).length ? e.message : e && !(e instanceof ApiError) ? 'Qualcosa è andato storto. Riprova.' : null);

export function LoginPage() {
  const login = useLogin();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const errs = fieldErrors(login.error);
  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); login.mutate({ email, password }, { onSuccess: () => navigate('/') }); }}>
        <BrandLogo height={34} />
        <h1>Bentornato</h1>
        <p className="lede">Accedi a Unitly per iniziare la giornata.</p>
        <div className="form">
          <TextField label="Email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errs.email} required autoFocus />
          <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errs.password} required />
          {formError(login.error) && <div className="form-error" role="alert">{formError(login.error)}</div>}
          <button className="btn btn-primary btn-block" disabled={login.isPending}>{login.isPending ? 'Accesso…' : 'Accedi'}</button>
        </div>
        <p className="auth-switch"><Link to="/forgot-password">Password dimenticata?</Link></p>
        <p className="auth-switch">Nuovo su Unitly? <Link to="/signup">Crea la tua azienda</Link></p>
      </form>
    </div>
  );
}

export function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [email, setEmail] = useState('');
  const errs = fieldErrors(forgot.error);
  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); forgot.mutate(email); }}>
        <BrandLogo height={34} />
        <h1>Password dimenticata</h1>
        <p className="lede">Inserisci la tua email: se corrisponde a un account, ti mandiamo un link per reimpostare la password.</p>
        {forgot.isSuccess ? (
          <p style={{ margin: 0 }}>Se l’indirizzo è registrato su Unitly, controlla la tua casella: è in arrivo un’email con il link per reimpostare la password (valido 1 ora).</p>
        ) : (
          <div className="form">
            <TextField label="Email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errs.email} required autoFocus />
            {formError(forgot.error) && <div className="form-error" role="alert">{formError(forgot.error)}</div>}
            <button className="btn btn-primary btn-block" disabled={forgot.isPending}>{forgot.isPending ? 'Un attimo…' : 'Continua'}</button>
          </div>
        )}
        <p className="auth-switch"><Link to="/login">Torna al login</Link></p>
      </form>
    </div>
  );
}

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const reset = useResetPassword();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const errs = fieldErrors(reset.error);
  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); reset.mutate({ token, password }, { onSuccess: () => navigate('/login') }); }}>
        <BrandLogo height={34} />
        <h1>Scegli una nuova password</h1>
        <div className="form">
          <TextField label="Nuova password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errs.password} hint="Almeno 12 caratteri." required minLength={12} autoFocus />
          {formError(reset.error) && (
            <div className="form-error" role="alert">
              {reset.error instanceof ApiError && reset.error.status === 404
                ? 'Questo link non è più valido: richiedine uno nuovo.'
                : formError(reset.error)}
            </div>
          )}
          <button className="btn btn-primary btn-block" disabled={reset.isPending}>{reset.isPending ? 'Salvataggio…' : 'Salva e accedi'}</button>
        </div>
        <p className="auth-switch"><Link to="/forgot-password">Richiedi un nuovo link</Link></p>
      </form>
    </div>
  );
}

export function SignupPage() {
  const signup = useSignup();
  const navigate = useNavigate();
  const [v, setV] = useState({ name: '', email: '', password: '', organizationName: '' });
  const errs = fieldErrors(signup.error);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); signup.mutate(v, { onSuccess: (r) => navigate(`/o/${r.organization.slug}`) }); }}>
        <BrandLogo height={34} />
        <h1>Crea la tua azienda</h1>
        <p className="lede">Uno spazio per persone, task e progetti. Inviterai il team subito dopo.</p>
        <div className="form">
          <TextField label="Nome e cognome" autoComplete="name" value={v.name} onChange={set('name')} error={errs.name} required autoFocus />
          <TextField label="Azienda" autoComplete="organization" value={v.organizationName} onChange={set('organizationName')} error={errs.organizationName} required />
          <TextField label="Email di lavoro" type="email" autoComplete="email" inputMode="email" value={v.email} onChange={set('email')} error={errs.email} required />
          <TextField label="Password" type="password" autoComplete="new-password" value={v.password} onChange={set('password')} error={errs.password} hint="Almeno 12 caratteri. Una frase è più sicura e facile da ricordare." required minLength={12} />
          {formError(signup.error) && <div className="form-error" role="alert">{formError(signup.error)}</div>}
          <button className="btn btn-primary btn-block" disabled={signup.isPending}>{signup.isPending ? 'Creazione…' : 'Crea account'}</button>
        </div>
        <p className="auth-switch">Hai già un account? <Link to="/login">Accedi</Link></p>
      </form>
    </div>
  );
}

/** Blocks the whole app until the person confirms the code sent to their email at signup - see App.tsx,
 * which renders this instead of the normal routes whenever `me.user.emailVerified` is false. Accounts that
 * already existed before this feature shipped were grandfathered as verified, so only brand-new signups
 * ever see this screen. */
export function VerifyEmailPage({ email }: { email: string }) {
  const verify = useVerifyEmail();
  const resend = useResendVerification();
  const logout = useLogout();
  const [code, setCode] = useState('');
  const [justResent, setJustResent] = useState(false);

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); verify.mutate(code); }}>
        <BrandLogo height={34} />
        <h1>Conferma la tua email</h1>
        <p className="lede">Abbiamo mandato un codice a 6 cifre a <strong>{email}</strong>. Inseriscilo qui sotto per continuare.</p>
        <div className="form">
          <TextField
            label="Codice di verifica" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            error={verify.error instanceof ApiError ? verify.error.errors.code ?? verify.error.message : undefined}
            required autoFocus
          />
          <button className="btn btn-primary btn-block" disabled={code.length !== 6 || verify.isPending}>{verify.isPending ? 'Verifico…' : 'Conferma'}</button>
        </div>
        <p className="auth-switch">
          Non l’hai ricevuto?{' '}
          <button type="button" style={linkButton} disabled={resend.isPending || justResent}
            onClick={() => resend.mutate(undefined, { onSuccess: () => { setJustResent(true); setTimeout(() => setJustResent(false), 30_000); } })}>
            {justResent ? 'Inviato di nuovo' : 'Invia un nuovo codice'}
          </button>
        </p>
        <p className="auth-switch"><button type="button" style={linkButton} onClick={() => logout.mutate()}>Esci e registrati con un’altra email</button></p>
      </form>
    </div>
  );
}

/** `me` is passed down from `App` (which already holds the single `useMe()` subscription): a second
 * concurrent observer on the same errored, `retry:false` query causes a refetch feedback loop. */
export function InvitePage({ me }: { me: { data: Me | undefined; isLoading: boolean } }) {
  const { token = '' } = useParams();
  const preview = useInvitationPreview(token);
  const accept = useAcceptInvitation(token);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const errs = fieldErrors(accept.error);

  if (preview.isLoading || me.isLoading) return <PageLoader />;
  if (preview.error || !preview.data) {
    return (
      <div className="auth">
        <div className="auth-card">
          <BrandLogo height={34} />
          <h1>Invito non valido</h1>
          <p className="lede">Il link è scaduto o è già stato usato. Chiedi a chi ti ha invitato di mandarne uno nuovo.</p>
          <Link className="btn btn-secondary btn-block" to="/">Vai a Unitly</Link>
        </div>
      </div>
    );
  }
  const inv = preview.data;
  const loggedIn = Boolean(me.data);
  const needsLogin = !loggedIn && inv.accountExists;
  const go = () => accept.mutate(loggedIn ? {} : { name, password }, { onSuccess: (r) => navigate(`/o/${r.organization.slug}`) });

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={(e) => { e.preventDefault(); go(); }}>
        <BrandLogo height={34} />
        <h1>Unisciti a {inv.organizationName}</h1>
        <p className="lede">Sei stato invitato come <strong>{ROLE_LABELS[inv.role]}</strong> con l’indirizzo {inv.email}.</p>
        {needsLogin ? (
          <>
            <p style={{ margin: 0 }}>Hai già un account Unitly con questa email: accedi e poi riapri questo link.</p>
            <Link className="btn btn-primary btn-block" to="/login">Accedi</Link>
          </>
        ) : (
          <div className="form">
            {!loggedIn && (
              <>
                <TextField label="Nome e cognome" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} error={errs.name} required autoFocus />
                <TextField label="Scegli una password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} error={errs.password} hint="Almeno 12 caratteri." required minLength={12} />
              </>
            )}
            {formError(accept.error) && <div className="form-error" role="alert">{formError(accept.error)}</div>}
            <button className="btn btn-primary btn-block" disabled={accept.isPending}>Accetta invito</button>
          </div>
        )}
      </form>
    </div>
  );
}
